import React, { useState } from 'react';
import {
    BranchFork16Regular,
    Tag16Regular,
    CalendarLtr16Regular,
    Star16Regular,
    Eye16Regular,
    EyeOff16Regular,
    Money16Regular,
    ArrowCircleDown16Regular,
    PeopleTeam16Regular,
    Person16Regular,
    LayerDiagonalAddRegular,
    Edit16Regular,
    Copy16Regular,
    Delete16Regular,
    CheckmarkCircle16Filled,
    CheckmarkCircle20Regular,
} from "@fluentui/react-icons";
import { Popover, PopoverSurface, PopoverTrigger, Spinner } from '@fluentui/react-components';
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { ContractConceptCategory } from "../../../../models/Entities/Contracts/ContractConceptCategory";
import ContractConceptCategorySelector from "../../../../commons/component/Inputs/ContractConceptCategorySelector/ContractConceptCategorySelector.Component";
import { useTranslation } from 'react-i18next';
import './ConceptList.Styles.css';
import { ErrorType } from "../../../../models/HttpError";
import {
    getSettlementLabel,
    getValuationLabel,
} from '../ConceptGroupedView/ConceptGroupedView.Functions';

export interface IProps {
    Concept: ContractConcept;
    index: number;
    ConceptNameById?: Record<number, string>;
    isCloning?: boolean;
    isDeleting: boolean;
    failonDeleting: boolean;
    error: ErrorType | undefined;
    onEdit?: (stage: ContractConcept) => void;
    onDelete?: (stage: ContractConcept) => void;
    onClone?: (stage: ContractConcept) => void;
    onToggleEnabled?: (concept: ContractConcept, enabled: boolean) => void;
    onToggleApproved?: (concept: ContractConcept, approved: boolean) => void;
    /** Cambia la categoría del concepto; `undefined` la quita. Sin esto el selector no se muestra. */
    onChangeCategory?: (concept: ContractConcept, category: ContractConceptCategory | undefined) => void;
    isChangingCategory?: boolean;
    /** Nombre de una categoría por id, desde el catálogo cargado. */
    categoryNameOf?: (categoryId: number) => string | undefined;
}

const formatPrice = (value?: number) =>
    typeof value === 'number' && !Number.isNaN(value)
        ? value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : '';

/**
 * Tarjeta de un concepto del contrato. Guía de diseño: superficie blanca con
 * borde, número de orden en círculo oscuro, nombre en 16 bold, etiquetas de
 * estado con contorno y tinte, importe a la derecha con el estilo "por cobrar".
 */
export const ConceptItemComponent = (props: IProps) => {
    const [t] = useTranslation();
    const cc = props.Concept;

    const isFixedValue = Number(cc.valuationType) === 1;
    const isPercentage = Number(cc.valuationType) === 3;
    const isByStage = Number(cc.valuationType) === 5;
    const currency = (cc.conceptCurrency || '').toUpperCase();

    const showPrice = isFixedValue && typeof cc.priceValue === 'number' && !Number.isNaN(cc.priceValue);
    const showPct = isPercentage && typeof cc.percentageValue === 'number' && !Number.isNaN(cc.percentageValue);
    const includedInConceptId = typeof cc.incluedInContractConceptId === 'number' ? cc.incluedInContractConceptId : 0;
    const includedInConceptName = includedInConceptId > 0 ? (props.ConceptNameById?.[includedInConceptId] || '').trim() : '';

    const executions = [
        { key: 'inFace', label: 'Presencial', active: !!cc.applyToInFaceExecution },
        { key: 'remote', label: 'Remoto', active: !!cc.applyToRemoteExecution },
        { key: 'phone', label: 'Telefónico', active: !!cc.applyToPhoneExecution },
    ].filter((item) => item.active);

    const appliesByBranch = !!cc.filterByBranches || (cc.branches?.length || 0) > 0;
    const branchNames = (cc.branches || [])
        .map((branch) => (branch.branchName || '').trim() || `#${branch.branchId}`)
        .filter((value, index, all) => value.length > 0 && all.indexOf(value) === index);
    const branchSummary = appliesByBranch
        ? (branchNames.length > 0 ? branchNames.join(', ') : t('conceptList.badges.noBranchAssigned', 'Sin rama asignada'))
        : t('conceptList.badges.allBranches', 'Todas las ramas');

    // Enabled defaults to true so concepts saved before the flag existed do not all
    // render as disabled; approval is opt-in, so it defaults to false.
    const isEnabled = cc.contractConceptEnabled !== false;
    const isApproved = cc.contractConceptApproved === true;

    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const hasCategory = !!cc.contractConceptCategoryId;
    // El nombre viene en el concepto; si el servidor no lo mandó, se busca en el catálogo cargado.
    const categoryName = (cc.contractConceptCategoryName || '').trim()
        || (hasCategory ? (props.categoryNameOf?.(cc.contractConceptCategoryId as number) || `#${cc.contractConceptCategoryId}`) : '');

    const handleOpenEdit = () => props.onEdit?.(cc);
    const stop = (event: React.SyntheticEvent) => event.stopPropagation();

    const features: { key: string; icon: JSX.Element; label: string; title?: string }[] = [
        ...(cc.groupWithInTheVisit ? [{ key: 'group', icon: <BranchFork16Regular />, label: t('conceptList.badges.groupByVisit', 'Agrupado') }] : []),
        ...(isByStage ? [{ key: 'visit', icon: <CalendarLtr16Regular />, label: t('conceptList.badges.byVisit', 'Por visita') }] : []),
        ...(cc.isIncludeInVisit ? [{ key: 'inc', icon: <Eye16Regular />, label: t('conceptList.badges.includeInVisit', 'Inc. en visita') }] : []),
        ...(cc.isIncludeInVisit && includedInConceptId > 0 ? [{
            key: 'incIn', icon: <Eye16Regular />,
            label: includedInConceptName
                ? t('conceptList.badges.includedInConceptName', { conceptName: includedInConceptName, defaultValue: 'Se liquida en: {{conceptName}}' })
                : t('conceptList.badges.includedInConcept', { conceptId: includedInConceptId, defaultValue: 'Se liquida en concepto #{{conceptId}}' })
        }] : []),
        ...(cc.isOptional ? [{ key: 'optional', icon: <Star16Regular />, label: t('conceptList.badges.optional', 'Opcional') }] : []),
        ...(cc.isManagedByCoordinator ? [{ key: 'coord', icon: <Person16Regular />, label: t('conceptList.badges.coordinator', 'Coordinador') }] : []),
        ...(cc.haveCost ? [{ key: 'cost', icon: <Money16Regular />, label: t('conceptList.badges.haveCost', 'Con costo') }] : []),
        ...(cc.haveWithdrawal ? [{ key: 'wh', icon: <ArrowCircleDown16Regular />, label: t('conceptList.badges.haveWithdrawal', 'Retención') }] : []),
        ...(cc.haveDivisionFee ? [{ key: 'div', icon: <PeopleTeam16Regular />, label: t('conceptList.badges.divisionFee', 'Div. honorarios') }] : []),
        ...(cc.haveOverhead ? [{ key: 'ovh', icon: <LayerDiagonalAddRegular />, label: t('conceptList.badges.overhead', 'Overhead') }] : []),
        {
            key: 'branch', icon: <BranchFork16Regular />, title: branchSummary,
            label: appliesByBranch ? `${t('conceptList.badges.byBranch', 'Por rama')}: ${branchSummary}` : t('conceptList.badges.allBranches', 'Todas las ramas')
        },
    ];

    return (
        <article
            className={['cci-card', isApproved ? 'cci-card--approved' : '', isEnabled ? '' : 'cci-card--disabled'].filter(Boolean).join(' ')}
            role="button"
            tabIndex={0}
            onClick={handleOpenEdit}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenEdit();
                }
            }}
        >
            {/* ── Orden + aprobación ── */}
            <div className="cci-order" onClick={stop} onKeyDown={stop}>
                <span className="cci-order__num" aria-label={t('conceptItem.props.concept')}>{props.index + 1}</span>
                <button
                    type="button"
                    className={`cci-approve${isApproved ? ' is-on' : ''}`}
                    aria-pressed={isApproved}
                    aria-label={isApproved ? t('conceptItem.flags.unapproveAction') : t('conceptItem.flags.approveAction')}
                    title={isApproved ? t('conceptItem.flags.unapproveAction') : t('conceptItem.flags.approveAction')}
                    onClick={() => props.onToggleApproved?.(cc, !isApproved)}
                >
                    {isApproved ? <CheckmarkCircle16Filled /> : <CheckmarkCircle20Regular />}
                </button>
            </div>

            {/* ── Cuerpo ── */}
            <div className="cci-body">
                <div className="cci-tags">
                    {isApproved && (
                        <span className="cci-tag cci-tag--success" title={t('conceptItem.flags.approvedTitle')}>
                            <CheckmarkCircle16Filled /> {t('conceptItem.flags.approved')}
                        </span>
                    )}
                    {!isEnabled && (
                        <span className="cci-tag cci-tag--muted" title={t('conceptItem.flags.disabledTitle')}>
                            <EyeOff16Regular /> {t('conceptItem.flags.disabled')}
                        </span>
                    )}
                    {/* Categoría: etiqueta que abre el selector en un popover. Sin categoría invita a categorizar. */}
                    {props.onChangeCategory && (
                        <Popover open={isCategoryOpen} onOpenChange={(_, data) => setIsCategoryOpen(data.open)} positioning="below-start" withArrow trapFocus>
                            <PopoverTrigger disableButtonEnhancement>
                                <button
                                    type="button"
                                    className={`cci-tag cci-tag--button${hasCategory ? ' cci-tag--info' : ' cci-tag--dashed'}`}
                                    title={hasCategory ? `${t('conceptItem.category.label', 'Categoría')}: ${categoryName}` : t('conceptItem.category.categorize', 'Categorizar')}
                                    disabled={props.isChangingCategory}
                                    onClick={(event) => { event.stopPropagation(); setIsCategoryOpen(true); }}
                                    onKeyDown={stop}
                                >
                                    {props.isChangingCategory ? <Spinner size="extra-tiny" /> : <Tag16Regular />}
                                    {hasCategory ? categoryName : t('conceptItem.category.categorize', 'Categorizar')}
                                </button>
                            </PopoverTrigger>
                            <PopoverSurface className="cci-category-pop" onClick={stop} onKeyDown={stop}>
                                <ContractConceptCategorySelector
                                    label={t('conceptItem.category.label', 'Categoría')}
                                    showManageButton
                                    inlineList
                                    hint={hasCategory ? t('conceptItem.category.hintChange', 'Quitá la etiqueta para dejarlo sin categoría.') : t('conceptItem.category.hintNew', 'Agrupa el concepto en filtros y resúmenes.')}
                                    SelectedCategories={cc.contractConceptCategoryId ? [{ id: cc.contractConceptCategoryId, name: categoryName }] : []}
                                    onSelectedCategories={(categories) => {
                                        setIsCategoryOpen(false);
                                        props.onChangeCategory?.(cc, categories[0]);
                                    }}
                                />
                            </PopoverSurface>
                        </Popover>
                    )}
                </div>

                <div className="cci-title">
                    <span className="cci-name">{cc.concept?.conceptName}</span>
                    {cc.concept?.conceptCode && <span className="cci-code">{cc.concept.conceptCode}</span>}
                </div>

                {/* Toda la información en una línea de chips separados por puntos */}
                <div className="cci-line">
                    <span className="cci-chip cci-chip--key" title={t('conceptForm.formFields.settlement', 'Liquidación')}>
                        {getSettlementLabel(cc.settlementSetting)}
                    </span>
                    <span className="cci-sep" aria-hidden>·</span>
                    <span className="cci-chip cci-chip--key" title={t('conceptForm.formFields.valorization', 'Valorización')}>
                        {getValuationLabel(cc.valuationType)}
                    </span>
                    {executions.length > 0 && (
                        <>
                            <span className="cci-sep" aria-hidden>·</span>
                            <span className="cci-chip" title="Aplica">{executions.map((item) => item.label).join(' / ')}</span>
                        </>
                    )}
                    {features.map((item) => (
                        <React.Fragment key={item.key}>
                            <span className="cci-sep" aria-hidden>·</span>
                            <span className="cci-chip" title={item.title ?? item.label}>{item.icon}{item.label}</span>
                        </React.Fragment>
                    ))}
                </div>

                {cc.concept?.conceptDescription && (
                    <p className="cci-desc" title={cc.concept.conceptDescription}>{cc.concept.conceptDescription}</p>
                )}

            </div>

            {/* ── Importe + acciones ── */}
            <div className="cci-aside">
                {showPrice && (
                    <div className="cci-value">
                        <span className="cci-value__label">{t('conceptForm.formFields.value', 'Valor')}</span>
                        <span className="cci-value__amount">{currency} {formatPrice(cc.priceValue)}</span>
                    </div>
                )}
                {showPct && (
                    <div className="cci-value">
                        <span className="cci-value__label">{t('conceptForm.formFields.value', 'Valor')}</span>
                        <span className="cci-value__amount">{cc.percentageValue}%</span>
                    </div>
                )}
                <div className="cci-actions" onClick={stop} onKeyDown={stop}>
                    <button
                        type="button"
                        className="cci-icon-btn"
                        aria-label={isEnabled ? t('conceptItem.flags.disableAction') : t('conceptItem.flags.enableAction')}
                        title={isEnabled ? t('conceptItem.flags.disableAction') : t('conceptItem.flags.enableAction')}
                        onClick={() => props.onToggleEnabled?.(cc, !isEnabled)}
                    >
                        {isEnabled ? <Eye16Regular /> : <EyeOff16Regular />}
                    </button>
                    <button
                        type="button"
                        className="cci-icon-btn"
                        aria-label={t('conceptItem.actions.clone', 'Clonar concepto')}
                        title={t('conceptItem.actions.clone', 'Clonar concepto')}
                        disabled={props.isCloning}
                        onClick={() => props.onClone?.(cc)}
                    >
                        {props.isCloning ? <Spinner size="extra-tiny" /> : <Copy16Regular />}
                    </button>
                    <button type="button" className="cci-icon-btn" aria-label="Editar" title="Editar" onClick={() => props.onEdit?.(cc)}>
                        <Edit16Regular />
                    </button>
                    <DeleteConceptButton
                        name={cc.concept?.conceptName || ''}
                        isDeleting={props.isDeleting}
                        onConfirm={() => props.onDelete?.(cc)}
                    />
                </div>
            </div>

        </article>
    );
};

/** Eliminar con confirmación en el lugar: dos clics, sin modal. */
const DeleteConceptButton = ({ name, isDeleting, onConfirm }: { name: string; isDeleting: boolean; onConfirm: () => void }) => {
    const [t] = useTranslation();
    const [confirming, setConfirming] = useState(false);

    if (!confirming) {
        return (
            <button
                type="button"
                className="cci-icon-btn cci-icon-btn--danger"
                aria-label={t('conceptItem.confirmationButton.deleteButton', 'Eliminar')}
                title={t('conceptItem.confirmationButton.deleteButton', 'Eliminar')}
                disabled={isDeleting}
                onClick={() => setConfirming(true)}
            >
                <Delete16Regular />
            </button>
        );
    }

    return (
        <span className="cci-confirm" role="alertdialog" aria-label={t('conceptItem.confirmationButton.tittle', 'Eliminar concepto')}>
            <span className="cci-confirm__text" title={name}>{t('conceptItem.confirmationButton.deleteConcept', 'Eliminar ')}{name}</span>
            <button type="button" className="cci-btn cci-btn--danger" disabled={isDeleting} onClick={() => { onConfirm(); setConfirming(false); }}>
                {isDeleting && <Spinner size="extra-tiny" />} {t('conceptItem.confirmationButton.deleteButton', 'Eliminar')}
            </button>
            <button type="button" className="cci-btn" disabled={isDeleting} onClick={() => setConfirming(false)}>
                {t('conceptItem.confirmationButton.cancelButton', 'Cancelar')}
            </button>
        </span>
    );
};
